
import { toast } from 'sonner';
import { getValidToken, handleUnauthorized, handleSessionRevoked, isBffMode, isSessionAlive } from '@/features/auth';
import { config } from '@/shared/lib/config';
import { createFaultCircuit } from './fault-circuit';
import { createRequestGuard, DEFAULT_REQUEST_TIMEOUT_MS, isAbortError } from './request-signal';
import { telemetryUrl } from './report-url';

const API_BASE = config.apiUrl;

export type ToastOption = false | { success?: string | boolean; error?: string | false };
export interface RequestOptions {
  toast?: ToastOption;
  signal?: AbortSignal;
  timeoutMs?: number;
  idempotencyKey?: string;
}

const DEFAULT_SUCCESS = 'Готово';

const SILENT_PATTERNS: RegExp[] = [
  /\/ai\//,
  /\/notifications/,
  /\/error-reports$/,
  /\/draft$/,
  /\/typing/,
  /\/favorites/,
  /\/attachments$/,
  /\/attachments\/[^/]+$/,
  /\/mass-incident\/reply$/,
  /^\/api\/v1\/wiki\//,
  /\/messages$/,
  /\/messages\/[^/]+$/,
  /\/messages\/delete$/,
];

function reqPath(url: string): string {
  return url.replace(API_BASE, '').split('?')[0];
}
function isSilentPath(path: string): boolean {
  return SILENT_PATTERNS.some((re) => re.test(path));
}

async function withToast<T>(
  mutating: boolean,
  url: string,
  opts: RequestOptions | undefined,
  exec: () => Promise<ApiResponse<T>>,
): Promise<ApiResponse<T>> {
  const t = opts?.toast;
  const muted = t === false || isSilentPath(reqPath(url));
  try {
    const res = await exec();
    if (mutating && !muted) {
      const custom = t && typeof t === 'object' ? t.success : undefined;
      const msg = custom === true ? DEFAULT_SUCCESS : custom;
      if (typeof msg === 'string' && msg) toast.success(msg);
    }
    return res;
  } catch (err) {
    if (isAbortError(err)) throw err;
    const authHandled = (err as { authHandled?: boolean } | null)?.authHandled === true;
    const eOpt = t && typeof t === 'object' ? t.error : undefined;
    if (!muted && !authHandled && eOpt !== false) {
      const generic = mutating ? 'Не удалось выполнить действие' : 'Не удалось загрузить данные';
      toast.error(typeof eOpt === 'string' ? eOpt : apiErrorText(err, generic));
    }
    throw err;
  }
}

export interface ApiResponse<T> {
  data: T;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
  meta?: Record<string, unknown>;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
  get serverFault(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

export function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) {
    return err.serverFault ? fallback : err.message || fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

async function buildAuthHeaders(forceRefresh = false): Promise<HeadersInit> {
  const token = await getValidToken(forceRefresh);
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

const STATUS_TEXT: Record<number, string> = {
  400: 'Сервер не принял запрос',
  401: 'Сессия истекла — войдите снова',
  403: 'Недостаточно прав',
  404: 'Сервер не поддерживает это действие — возможно, он ещё не обновлён',
  405: 'Сервер не поддерживает это действие — возможно, он ещё не обновлён',
  409: 'Данные изменились — обновите страницу',
  413: 'Слишком большой запрос',
  429: 'Слишком много запросов — повторите чуть позже',
};

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      message?: string;
      error?: { message?: string; code?: string };
    };
    const message = body?.error?.message ?? body?.message ?? STATUS_TEXT[response.status] ?? `Ошибка запроса (${response.status})`;
    const code = typeof body?.error?.code === 'string' ? body.error.code : undefined;
    throw new ApiRequestError(message, response.status, code);
  }
  if (response.status === 204) {
    return { data: undefined as T };
  }
  const data = await response.json();
  return data as ApiResponse<T>;
}

type RequestInitWithMethod = RequestInit & { method: string };

const reportedNetworkIssues = new Map<string, number>();
const NETWORK_REPORT_THROTTLE_MS = 60_000;

const telemetryCircuit = createFaultCircuit();

function reportNetworkIssue(url: string, init: RequestInitWithMethod, detail: string): void {
  try {
    if (url.includes('/error-reports')) return;
    if (telemetryCircuit.recordFault()) return;
    const path = url.replace(API_BASE, '').split('?')[0];
    const key = `${init.method} ${path} ${detail}`;
    const now = Date.now();
    const last = reportedNetworkIssues.get(key);
    if (last && now - last < NETWORK_REPORT_THROTTLE_MS) return;
    reportedNetworkIssues.set(key, now);
    const auth = (init.headers as Record<string, string> | undefined)?.['Authorization'];
    void fetch(`${API_BASE}/api/v1/error-reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(auth ? { Authorization: auth } : {}) },
      body: JSON.stringify({
        kind: 'network',
        message: `Сетевая ошибка: ${init.method} ${path} — ${detail}`,
        url: telemetryUrl(window.location.href),
        userAgent: navigator.userAgent,
      }),
    }).catch(() => undefined);
  } catch {
  }
}

async function doRequest<T>(
  url: string,
  init: RequestInitWithMethod,
  retried = false
): Promise<ApiResponse<T>> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    if (init.signal?.aborted) throw err;
    throw new ApiRequestError('Нет соединения с сервером', 0);
  }
  if (response.status >= 500) {
    reportNetworkIssue(url, init, `HTTP ${response.status}`);
  } else {
    telemetryCircuit.recordSuccess();
  }
  if (response.status === 401) {
    const peek = (await response.clone().json().catch(() => ({}))) as {
      error?: { code?: string };
    };
    if (peek?.error?.code === 'SESSION_REVOKED') {
      handleSessionRevoked();
      return handleResponseFlagged<T>(response);
    }
    const prevAuth = (init.headers as Record<string, string> | undefined)?.['Authorization'];
    if (!prevAuth) {
      if (isBffMode() && isSessionAlive()) handleUnauthorized();
      return handleResponseFlagged<T>(response);
    }
    if (!retried) {
      const headersWithNewToken = await buildAuthHeaders(true);
      const nextAuth = (headersWithNewToken as Record<string, string>)['Authorization'];
      if (nextAuth && nextAuth !== prevAuth) {
        const retryInit: RequestInitWithMethod = {
          ...init,
          headers: { ...(init.headers as Record<string, string>), ...headersWithNewToken },
        };
        if (init.body instanceof FormData) {
          delete (retryInit.headers as Record<string, string>)['Content-Type'];
        }
        return doRequest<T>(url, retryInit, true);
      }
    }
    if (!isSessionAlive()) handleUnauthorized();
    return handleResponseFlagged<T>(response);
  }
  return handleResponse<T>(response);
}

async function send<T>(
  url: string,
  init: RequestInitWithMethod,
  opts: RequestOptions | undefined,
): Promise<ApiResponse<T>> {
  const guard = createRequestGuard(opts?.signal, opts?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS);
  try {
    return await doRequest<T>(url, { ...init, signal: guard.signal });
  } catch (err) {
    if (guard.timedOut() && !(err instanceof ApiRequestError)) {
      throw new ApiRequestError('Превышено время ожидания ответа', 0);
    }
    if (opts?.signal?.aborted && !isAbortError(err)) {
      throw new DOMException('The operation was aborted', 'AbortError');
    }
    throw err;
  } finally {
    guard.dispose();
  }
}

async function handleResponseFlagged<T>(response: Response): Promise<ApiResponse<T>> {
  try {
    return await handleResponse<T>(response);
  } catch (e) {
    if (e && typeof e === 'object') (e as { authHandled?: boolean }).authHandled = true;
    throw e;
  }
}

export const api = {
  async postForm<T>(path: string, body: FormData, opts?: RequestOptions): Promise<ApiResponse<T>> {
    const url = API_BASE ? `${API_BASE}${path}` : path;
    return withToast(true, url, opts, async () => {
      const headers = await buildAuthHeaders() as Record<string, string>;
      delete headers['Content-Type'];
      return send<T>(url, { method: 'POST', headers, body }, opts);
    });
  },
  async get<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
    opts?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    const search = params
      ? '?' +
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const url = API_BASE ? `${API_BASE}${path}${search}` : `${path}${search}`;
    return withToast(false, url, opts, async () =>
      send<T>(url, { method: 'GET', headers: await buildAuthHeaders() }, opts),
    );
  },

  async post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<ApiResponse<T>> {
    const url = API_BASE ? `${API_BASE}${path}` : path;
    return withToast(true, url, opts, async () =>
      send<T>(
        url,
        {
          method: 'POST',
          headers: {
            ...(await buildAuthHeaders()),
            ...(opts?.idempotencyKey ? { 'Idempotency-Key': opts.idempotencyKey } : {}),
          },
          body: body ? JSON.stringify(body) : undefined,
        },
        opts,
      ),
    );
  },

  async put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<ApiResponse<T>> {
    const url = API_BASE ? `${API_BASE}${path}` : path;
    return withToast(true, url, opts, async () =>
      send<T>(
        url,
        {
          method: 'PUT',
          headers: await buildAuthHeaders(),
          body: body ? JSON.stringify(body) : undefined,
        },
        opts,
      ),
    );
  },

  async patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<ApiResponse<T>> {
    const url = API_BASE ? `${API_BASE}${path}` : path;
    return withToast(true, url, opts, async () =>
      send<T>(
        url,
        {
          method: 'PATCH',
          headers: await buildAuthHeaders(),
          body: body ? JSON.stringify(body) : undefined,
        },
        opts,
      ),
    );
  },

  async delete<T>(path: string, opts?: RequestOptions): Promise<ApiResponse<T>> {
    const url = API_BASE ? `${API_BASE}${path}` : path;
    return withToast(true, url, opts, async () =>
      send<T>(url, { method: 'DELETE', headers: await buildAuthHeaders() }, opts),
    );
  },
};
